import type { MouseEventHandler, ReactNode } from "react"
import Box from "@cloudscape-design/components/box"
import Container from "@cloudscape-design/components/container"
import Icon, { type IconProps } from "@cloudscape-design/components/icon"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { Link } from "react-router-dom"
import "./StatTile.css"

interface StatTileProps {
  to: string
  iconName: IconProps.Name
  iconVariant: IconProps.Variant
  value: ReactNode
  label: string
  /**
   * Optional click handler. `to` is still rendered as the anchor href (keeping the tile a real,
   * shareable link), so a handler that filters in-page should `preventDefault()` to suppress
   * navigation.
   */
  onClick?: MouseEventHandler<HTMLAnchorElement>
}

/**
 * A compact clickable metric tile (icon + value + label) that navigates to `to`. Shared between the
 * portfolio dashboard health panel and the study-level health panel.
 */
const StatTile = ({ to, iconName, iconVariant, value, label, onClick }: StatTileProps) => (
  <Link to={to} className="stat-tile" onClick={onClick}>
    <Container disableContentPaddings>
      <Box padding="s">
        <SpaceBetween direction="horizontal" size="m" alignItems="center">
          <Icon name={iconName} variant={iconVariant} size="big" />
          <div>
            <Box fontSize="heading-xl" fontWeight="bold" padding={{ vertical: "xxxs" }}>
              {value}
            </Box>
            <Box variant="small" color="text-body-secondary">
              {label}
            </Box>
          </div>
        </SpaceBetween>
      </Box>
    </Container>
  </Link>
)

export default StatTile
